/**
 * One navigation definition for the whole shell (navigation concept v1.1 §2,
 * §8): the sidebar groups, the breadcrumb trail, the active-state rules and the
 * keyboard-shortcut table all read this file.
 *
 * Before this existed the sidebar, the header and `shortcuts.tsx` each kept
 * their own hand-maintained list, and they drifted: Alt+4 was documented as
 * "Users" in the overlay while it actually navigated to `/settings`. A label,
 * a path or a shortcut now changes in exactly one place.
 */
import {
  ChartColumn,
  ClipboardList,
  FolderKanban,
  KeyRound,
  LayoutDashboard,
  ListChecks,
  type LucideIcon,
  Settings,
  UserRound,
} from "lucide-react";

/** Sidebar groups, in the order the sidebar renders them (§2). */
export type NavGroupId = "workspace" | "monitoring" | "administration";

export interface NavGroup {
  readonly id: NavGroupId;
  readonly label: string;
}

/**
 * The `pinned` group is not listed here: it is populated from the viewer's own
 * accessible resources rather than from this static table. The sidebar renders
 * Workspace and Monitoring, then Pinned, then Administration, so the approved
 * order is the group order with pinned resources spliced in before Settings.
 */
export const NAV_GROUPS: readonly NavGroup[] = [
  { id: "workspace", label: "Workspace" },
  { id: "monitoring", label: "Monitoring" },
  { id: "administration", label: "Administration" },
];

export const PINNED_GROUP_LABEL = "Pinned";

/**
 * How a destination decides whether it is the selected section.
 *
 * - `exact` — the canonical path and nothing below it. Global Runs uses this so
 *   an agent's own Runs tab never lights up the global destination (§7).
 * - `section` — the path and everything under it, which is what makes a
 *   Settings child highlight Settings globally (§2).
 * - `projects` — the Projects directory plus every project and agent page,
 *   because an agent page belongs to Projects (§2, §7).
 */
export type NavMatch = "exact" | "section" | "projects";

export interface NavDestination {
  /** Stable key; also what the shortcut table refers to. */
  readonly id: string;
  readonly to: string;
  readonly label: string;
  readonly icon: LucideIcon;
  readonly group: NavGroupId;
  /** Admin-only destinations are filtered out for everyone else (§8). */
  readonly adminOnly: boolean;
  readonly match: NavMatch;
}

const OVERVIEW: NavDestination = {
  id: "overview",
  to: "/",
  label: "Overview",
  icon: LayoutDashboard,
  group: "workspace",
  adminOnly: false,
  match: "exact",
};

const PROJECTS: NavDestination = {
  id: "projects",
  to: "/projects",
  label: "Projects",
  icon: FolderKanban,
  group: "workspace",
  adminOnly: false,
  match: "projects",
};

const RUNS: NavDestination = {
  id: "runs",
  to: "/runs",
  label: "Runs",
  icon: ListChecks,
  group: "workspace",
  adminOnly: false,
  match: "exact",
};

const ACTION_LOG: NavDestination = {
  id: "action-log",
  to: "/action-log",
  label: "Action log",
  icon: ClipboardList,
  group: "monitoring",
  adminOnly: true,
  match: "exact",
};

const USAGE: NavDestination = {
  id: "usage",
  to: "/usage",
  label: "Usage & costs",
  icon: ChartColumn,
  group: "monitoring",
  adminOnly: false,
  match: "exact",
};

const SETTINGS: NavDestination = {
  id: "settings",
  to: "/settings",
  label: "Settings",
  icon: Settings,
  group: "administration",
  adminOnly: true,
  match: "section",
};

/** The six stable primary destinations (§2). */
export const NAV_DESTINATIONS: readonly NavDestination[] = [
  OVERVIEW,
  PROJECTS,
  RUNS,
  ACTION_LOG,
  USAGE,
  SETTINGS,
];

/** "View all projects" under the pinned group; Projects stays the directory. */
export const ALL_PROJECTS_LABEL = "View all projects";

/** At most this many projects are pinned, however many are accessible (§2). */
export const MAX_PINNED_PROJECTS = 5;

/**
 * At most this many agents render inside one pinned project branch (#147).
 *
 * A project with hundreds of agents must not turn the sidebar into its own
 * directory: the branch shows the first N in server order plus the agent the
 * route names, and links the project page for the rest.
 */
export const MAX_PINNED_AGENTS_PER_PROJECT = 5;

export interface AccountMenuEntry {
  readonly to: string;
  readonly label: string;
  readonly icon: LucideIcon;
}

/**
 * The footer account menu (§2). Account belongs beside the signed-in identity,
 * not among the operational destinations, and the token screen is the existing
 * one — `#api-access` is an anchor into it, not a second implementation.
 */
export const ACCOUNT_MENU: readonly AccountMenuEntry[] = [
  { to: "/account", label: "My account", icon: UserRound },
  { to: "/account#api-access", label: "API access & tokens", icon: KeyRound },
];

export function permittedDestinations(admin: boolean): readonly NavDestination[] {
  return NAV_DESTINATIONS.filter(function permitted(destination): boolean {
    return admin || !destination.adminOnly;
  });
}

export function destinationsInGroup(group: NavGroupId, admin: boolean): readonly NavDestination[] {
  return permittedDestinations(admin).filter(function inGroup(destination): boolean {
    return destination.group === group;
  });
}

export function isDestinationActive(destination: NavDestination, pathname: string): boolean {
  if (destination.match === "projects") return isProjectsSection(pathname);
  if (destination.match === "section") {
    return pathname === destination.to || pathname.startsWith(`${destination.to}/`);
  }
  return pathname === destination.to;
}

/** The destination the given path selects, or nothing outside the six. */
export function activeDestination(pathname: string, admin: boolean): NavDestination | undefined {
  return permittedDestinations(admin).find(function active(destination): boolean {
    return isDestinationActive(destination, pathname);
  });
}

function isProjectsSection(pathname: string): boolean {
  return (
    pathname === PROJECTS.to ||
    pathname.startsWith(`${PROJECTS.to}/`) ||
    pathname.startsWith("/agents/")
  );
}

/** The agent id the path names, for the pinned group and the breadcrumb. */
export function routeAgentId(pathname: string): string | undefined {
  const raw = /^\/agents\/([^/]+)/u.exec(pathname)?.[1];
  return raw === undefined ? undefined : decodeURIComponent(raw);
}

/** The project id the path names, which is the pinned branch to open. */
export function routeProjectId(pathname: string): string | undefined {
  const raw = /^\/projects\/([^/]+)/u.exec(pathname)?.[1];
  return raw === undefined ? undefined : decodeURIComponent(raw);
}

/* ------------------------------------------------------------------ *
 * Keyboard shortcuts
 * ------------------------------------------------------------------ */

interface ShortcutTarget {
  readonly to: string;
  readonly label: string;
}

export interface ShortcutBinding extends ShortcutTarget {
  readonly keys: string;
}

/** Account is a footer entry rather than a destination, so it is named here. */
const ACCOUNT_SHORTCUT: ShortcutTarget = { to: "/account", label: "My account" };

/**
 * Alt+1…7 for an admin and Alt+1…5 for everyone else, in sidebar reading
 * order. Alt+1 (Overview), Alt+2 (Action log, the renamed Proposals) and Alt+7
 * (Account) keep the paths they had; Alt+3…6 are the documented remap, because
 * Audit, Users and Sandbox images are no longer primary destinations. The
 * administrative pages they moved to are reachable through Alt+6 Settings,
 * which only the admin list carries.
 */
const ADMIN_SHORTCUT_TARGETS: readonly ShortcutTarget[] = [
  OVERVIEW,
  ACTION_LOG,
  PROJECTS,
  RUNS,
  USAGE,
  SETTINGS,
  ACCOUNT_SHORTCUT,
];

const USER_SHORTCUT_TARGETS: readonly ShortcutTarget[] = [
  OVERVIEW,
  ACCOUNT_SHORTCUT,
  PROJECTS,
  RUNS,
  USAGE,
];

function numbered(targets: readonly ShortcutTarget[]): readonly ShortcutBinding[] {
  return targets.map(function numberedBinding(target, index): ShortcutBinding {
    return { keys: `Alt+${String(index + 1)}`, to: target.to, label: target.label };
  });
}

export const ADMIN_SHORTCUTS: readonly ShortcutBinding[] = numbered(ADMIN_SHORTCUT_TARGETS);
export const USER_SHORTCUTS: readonly ShortcutBinding[] = numbered(USER_SHORTCUT_TARGETS);

export function globalShortcuts(admin: boolean): readonly ShortcutBinding[] {
  return admin ? ADMIN_SHORTCUTS : USER_SHORTCUTS;
}

/** The remap the overlay states out loud, so a changed key is never silent. */
export const SHORTCUT_REMAP_NOTE =
  "Alt+3 to Alt+6 moved with the new navigation: Audit, Users and Sandbox images now live inside Settings (Alt+6).";

/** Agent tab bindings, unchanged (§8: established bindings keep working). */
export const AGENT_SHORTCUTS: readonly {
  readonly key: string;
  readonly section: string;
  readonly label: string;
}[] = [
  { key: "q", section: "chat", label: "Agent chat" },
  { key: "w", section: "runs", label: "Agent runs" },
  { key: "e", section: "sessions", label: "Agent sessions" },
  { key: "r", section: "stats", label: "Usage & costs" },
  { key: "t", section: "config", label: "Configuration" },
];

export function agentShortcutSection(key: string): string | undefined {
  return AGENT_SHORTCUTS.find(function bound(entry): boolean {
    return entry.key === key;
  })?.section;
}

/* ------------------------------------------------------------------ *
 * Settings hub sections
 * ------------------------------------------------------------------ */

export interface SettingsSection {
  readonly to: string;
  readonly label: string;
  /** One sentence naming what this section decides, shown under its label. */
  readonly purpose: string;
  /**
   * NavLink `end` matching. Needed where one section's path prefixes another
   * (the hub root ↔ every child) so only the visited one is current.
   */
  readonly end?: boolean;
}

/** A heading in the Settings hub nav, with the sections it gathers (§3). */
export interface SettingsSectionGroup {
  readonly label: string;
  readonly sections: readonly SettingsSection[];
}

/**
 * The Settings hub's local navigation, grouped under the five approved
 * headings (navigation concept v1.1 §3).
 *
 * Data sources, Google accounts and Rejected content are deliberately absent:
 * they are Connections tabs and a Safety subsection rather than peers of
 * Models & providers, and listing them flat is what made the old list read as
 * sixteen equally important screens. Their routes stay registered (see
 * `SETTINGS_SUBROUTES`), so every bookmark keeps answering.
 */
export const SETTINGS_SECTION_GROUPS: readonly SettingsSectionGroup[] = [
  {
    label: "General",
    sections: [
      {
        to: "/settings",
        label: "Setup & defaults",
        purpose: "First-run readiness and the defaults new agents inherit.",
        end: true,
      },
    ],
  },
  {
    label: "AI & connections",
    sections: [
      {
        to: "/settings/providers",
        label: "Models & providers",
        purpose: "Provider endpoints, credentials and the model catalogue.",
      },
      {
        to: "/settings/connections",
        label: "Connections",
        purpose: "Data sources, company APIs, Google accounts, search, vision and email.",
      },
    ],
  },
  {
    label: "Runtime",
    sections: [
      {
        to: "/settings/sandbox-images",
        label: "Sandbox images",
        purpose: "Container images and build profiles runs execute in.",
      },
      {
        to: "/settings/capacity",
        label: "Execution & capacity",
        purpose: "Concurrency, admission and queue limits for runs.",
      },
      {
        to: "/settings/safety",
        label: "Safety",
        purpose: "Admission policy, content guard and the rejected content it recorded.",
      },
    ],
  },
  {
    label: "Access & security",
    sections: [
      {
        to: "/settings/users",
        label: "Users & access",
        purpose: "Accounts and the grants that scope what each one may reach.",
      },
      {
        to: "/settings/authentication",
        label: "Authentication",
        purpose: "Sign-in methods, session lifetime and token policy.",
      },
      {
        to: "/settings/secrets",
        label: "Secrets",
        purpose: "Write-only credential store every ${secret:NAME} reference resolves against.",
      },
      {
        to: "/settings/audit",
        label: "Audit log",
        purpose: "Recorded platform configuration, access, lifecycle and administrative events.",
      },
    ],
  },
  {
    label: "Maintenance",
    sections: [
      {
        to: "/settings/notifications",
        label: "Notifications",
        purpose: "Where operational alerts are delivered.",
      },
      {
        to: "/settings/retention",
        label: "Retention & storage",
        purpose: "How long transcripts, runs, audit and usage records are kept.",
      },
      {
        to: "/settings/maintenance/archive-requests",
        label: "Archive requests",
        purpose: "Pending and decided requests to retire an agent or project.",
      },
      {
        to: "/settings/system-status",
        label: "System status",
        purpose: "What the running platform reports about itself, read-only.",
      },
    ],
  },
];

/**
 * The flattened section list, kept as the compatibility view for callers that
 * want every hub destination without caring which heading it sits under.
 */
export const SETTINGS_SECTIONS: readonly SettingsSection[] = SETTINGS_SECTION_GROUPS.flatMap(
  function sectionsOf(group): readonly SettingsSection[] {
    return group.sections;
  },
);

/**
 * Which hub section OWNS each compatibility path (#146).
 *
 * Regrouping moved three screens out of the flat nav without unregistering
 * their paths, which left a hole: on `/settings/data-sources` no local entry
 * was current and the breadcrumb hung the page off Settings directly, so a
 * visitor arriving on an old bookmark could not see which section they were
 * in. The owner named here is what the hub marks current AND what the
 * breadcrumb names as the parent, so one table fixes both and they cannot
 * drift apart.
 *
 * `/settings/connections/google` needs no entry: its path already sits under
 * its owner, so the prefix rule below covers it.
 */
const SETTINGS_COMPATIBILITY_OWNERS: Readonly<Record<string, string>> = {
  "/settings/data-sources": "/settings/connections",
  "/settings/rejected-content": "/settings/safety",
};

/**
 * Registered `/settings/*` routes that are not hub peers. They must stay in
 * the route table — `isShellRoute` gates every breadcrumb link — without
 * appearing in the nav.
 *
 * Derived from the owner map so a compatibility path cannot be given an owner
 * and then forgotten here, which would silently unregister its route. The
 * Google alias is listed separately because its path already sits under its
 * owner and therefore needs no entry in that map.
 */
const SETTINGS_SUBROUTES: readonly string[] = [
  ...Object.keys(SETTINGS_COMPATIBILITY_OWNERS),
  "/settings/connections/google",
];

/**
 * Whether `section` is the hub entry the given path belongs to — the canonical
 * path, anything under it, or a compatibility path it owns.
 *
 * The hub renders this instead of `NavLink`'s own matching, because NavLink
 * only knows about path prefixes and a compatibility alias is not one.
 */
export function settingsSectionActive(section: SettingsSection, pathname: string): boolean {
  // The hub root prefixes every child, so it matches exactly or not at all.
  if (section.end === true) return pathname === section.to;
  if (pathname === section.to || pathname.startsWith(`${section.to}/`)) return true;
  return SETTINGS_COMPATIBILITY_OWNERS[pathname] === section.to;
}

/** The hub label of a section path, for the breadcrumb's parent crumb. */
function settingsSectionLabel(path: string): string | undefined {
  return SETTINGS_SECTIONS.find(function byPath(section): boolean {
    return section.to === path;
  })?.label;
}

/**
 * The project sub-paths the Settings tab owns (#146). `/access` used to be its
 * own tab; it still renders the access view, inside Settings, so Settings is
 * the tab that has to read as current — otherwise the bookmark lands on a
 * project page with no selected tab at all.
 */
const PROJECT_SETTINGS_SEGMENTS: readonly string[] = ["settings", "access"];

export function projectSettingsActive(pathname: string): boolean {
  const segments = pathSegments(pathname);
  if (segments[0] !== "projects" || segments.length !== 3) return false;
  return PROJECT_SETTINGS_SEGMENTS.includes(segments[2] ?? "");
}

/**
 * `NavLink`'s own matching, for the agent and project tab bars that now state
 * their current tab themselves (`SectionNav`, #163): the path itself, or —
 * unless `end` — anything under it at a segment boundary, compared without
 * case the way NavLink compares. So `/agents/a/runs/<uid>` keeps Runs current,
 * and `ops` never lights up for `ops-reporter`.
 */
export function sectionPathActive(pathname: string, to: string, end = false): boolean {
  const path = pathname.toLowerCase();
  const target = to.toLowerCase();
  if (path === target) return true;
  return !end && path.startsWith(`${target}/`);
}

/* ------------------------------------------------------------------ *
 * Breadcrumbs and route metadata
 * ------------------------------------------------------------------ */

export interface Crumb {
  readonly label: string;
  /**
   * Absent on the current page, and on any ancestor segment that is not itself
   * a route — `/integrations` is a path prefix only, so linking it would land
   * on the 404 page.
   */
  readonly href?: string;
}

/**
 * Display names the breadcrumb cannot derive from the URL. They come from the
 * React Query caches the page already fills, never from a fresh request, and
 * each is optional: an unresolvable name falls back to the route identifier
 * rather than inventing one.
 */
export interface BreadcrumbContext {
  readonly projectName?: string;
  readonly agentName?: string;
  /**
   * The project an agent page's agent belongs to (#147). The agent payload
   * carries it, so an agent trail can pass through its own project instead of
   * hanging off the Projects directory.
   */
  readonly projectId?: string;
}

/**
 * Segments whose URL spelling is not the label we want to show. Anything not
 * listed falls through to the decoded segment, which is what identifier
 * segments (an agent id, a run uid) need.
 *
 * Settings children are labelled here rather than read out of
 * `SETTINGS_SECTIONS` — a segment is not always a section (`maintenance` is a
 * bare prefix) — but the wording now matches: §3 asks breadcrumb, selected
 * entry and page title to name the same place, so "Audit log", "Users &
 * access", "Usage & costs", "Configuration" and "System status" are spelled
 * once. The legacy aliases keep their entries so an old link still reads
 * sensibly on its way through the redirect.
 */
const SEGMENT_LABELS: Readonly<Record<string, string>> = {
  access: "Access",
  account: "Account",
  "action-log": ACTION_LOG.label,
  agents: "Agents",
  "archive-proposals": "Archive requests",
  "archive-requests": "Archive requests",
  audit: "Audit log",
  authentication: "Authentication",
  callback: "Callback",
  capacity: "Execution & capacity",
  chat: "Chat",
  config: "Configuration",
  connections: "Connections",
  "data-sources": "Data sources",
  google: "Google accounts",
  integrations: "Integrations",
  maintenance: "Maintenance",
  // `/projects/:id/agents/new`, the create wizard (#163 PR 3 audit).
  new: "New agent",
  notifications: "Notifications",
  projects: PROJECTS.label,
  proposals: ACTION_LOG.label,
  providers: "Models & providers",
  "rejected-content": "Rejected Gmail content",
  retention: "Retention & storage",
  runs: RUNS.label,
  safety: "Safety",
  "sandbox-images": "Sandbox images",
  secrets: "Secrets",
  sessions: "Sessions",
  settings: SETTINGS.label,
  stats: USAGE.label,
  "system-status": "System status",
  usage: USAGE.label,
  users: "Users & access",
};

/**
 * Every registered `/settings/*` path as one pattern: the grouped hub sections
 * plus the subroutes that are reachable without being nav peers. Regrouping
 * the nav must not unregister a route — a path missing here loses its
 * breadcrumb link, which is how `/settings/data-sources` would silently stop
 * being linkable the moment it left the flat list.
 */
function settingsRoutePattern(): RegExp {
  const children = [
    ...SETTINGS_SECTIONS.map(function sectionPath(section): string {
      return section.to;
    }),
    ...SETTINGS_SUBROUTES,
  ]
    .filter(function child(path): boolean {
      return path !== SETTINGS.to;
    })
    .map(function childPath(path): string {
      return path.slice(`${SETTINGS.to}/`.length);
    });
  return new RegExp(`^/settings(/(?:${children.join("|")}))?$`, "u");
}

/**
 * The routes `AppRoutes` registers inside the shell, as patterns. Keep this in
 * step with `AppRoutes` in `src/App.tsx`: a breadcrumb only links a path that
 * matches one of these, so an unregistered prefix cannot send anyone to the
 * not-found page. `/login` is deliberately absent — it renders outside the
 * shell and therefore never gets a breadcrumb.
 */
const SHELL_ROUTES: readonly RegExp[] = [
  /^\/$/u,
  /^\/account$/u,
  /^\/action-log$/u,
  /^\/usage$/u,
  /^\/runs$/u,
  /^\/projects$/u,
  /^\/projects\/[^/]+$/u,
  /^\/projects\/[^/]+\/(?:agents|runs|usage|access|settings)$/u,
  /^\/projects\/[^/]+\/agents\/new$/u,
  /^\/users$/u,
  /^\/sandbox-images$/u,
  settingsRoutePattern(),
  /^\/proposals$/u,
  /^\/archive-proposals$/u,
  /^\/integrations\/google$/u,
  /^\/integrations\/google\/callback$/u,
  /^\/audit$/u,
  /^\/agents\/[^/]+$/u,
  /^\/agents\/[^/]+\/(?:chat|runs|sessions|stats|config)$/u,
  /^\/agents\/[^/]+\/runs\/[^/]+$/u,
];

/** True when `path` resolves to a registered route rather than a bare prefix. */
export function isShellRoute(path: string): boolean {
  return SHELL_ROUTES.some(function matches(pattern): boolean {
    return pattern.test(path);
  });
}

function pathSegments(pathname: string): readonly string[] {
  return pathname.split("/").filter(function present(segment): boolean {
    return segment !== "";
  });
}

/** Appends `segments` to `crumbs`, linking only those that are routes. */
function appendSegments(
  crumbs: Crumb[],
  segments: readonly string[],
  basePath: string,
  labelFor: (segment: string, index: number) => string,
): void {
  let href = basePath;
  for (const [index, segment] of segments.entries()) {
    href = `${href}/${segment}`;
    const label = labelFor(segment, index);
    const last = index === segments.length - 1;
    crumbs.push(last || !isShellRoute(href) ? { label } : { label, href });
  }
}

function segmentLabel(segment: string): string {
  return SEGMENT_LABELS[segment] ?? decodeURIComponent(segment);
}

/**
 * An agent page belongs to Projects (§2, §7), so its trail passes through the
 * Projects directory rather than through a dead `/agents` prefix.
 *
 * When the agent record names its project (#147) the trail also names that
 * project: `Projects → project → agent → section`. The crumb links the project
 * page by id even when the catalog cache cannot resolve its name, because the
 * id is what the route already knows. An agent whose project is genuinely
 * unavailable keeps the shorter `Projects → agent` trail rather than inventing
 * a parent.
 */
function agentCrumbs(segments: readonly string[], context: BreadcrumbContext): readonly Crumb[] {
  const rawAgentId = segments[1];
  if (rawAgentId === undefined) return [{ label: SEGMENT_LABELS.agents ?? "Agents" }];
  const crumbs: Crumb[] = [{ label: PROJECTS.label, href: PROJECTS.to }];
  const agentLabel = context.agentName ?? decodeURIComponent(rawAgentId);
  const agentBase = `/agents/${rawAgentId}`;
  if (context.projectId !== undefined && context.projectId !== "") {
    crumbs.push({
      // An unresolved name falls back to the id the route knows, never a guess.
      label: context.projectName ?? context.projectId,
      href: `/projects/${encodeURIComponent(context.projectId)}`,
    });
  }
  const rest = segments.slice(2);
  if (rest.length === 0) {
    crumbs.push({ label: agentLabel });
    return crumbs;
  }
  crumbs.push({ label: agentLabel, href: agentBase });
  appendSegments(crumbs, rest, agentBase, segmentLabel);
  return crumbs;
}

/**
 * `Settings / <owning section> / <page>` for a compatibility path (#146).
 *
 * Without this the trail read `Settings / Data sources`, which says the page
 * is a section of the hub — exactly the claim regrouping withdrew. The owner
 * crumb is a link, so the trail is also the way back into the section the
 * page now lives in.
 */
function compatibilitySettingsCrumbs(pathname: string): readonly Crumb[] | undefined {
  const owner = SETTINGS_COMPATIBILITY_OWNERS[pathname];
  if (owner === undefined) return undefined;
  const ownerLabel = settingsSectionLabel(owner);
  if (ownerLabel === undefined) return undefined;
  const leaf = pathSegments(pathname).at(-1) ?? "";
  return [
    { label: SETTINGS.label, href: SETTINGS.to },
    { label: ownerLabel, href: owner },
    { label: segmentLabel(leaf) },
  ];
}

/**
 * `Projects / <project> / Settings / Access` (#146): the access view is part
 * of the project's Settings tab now, so the trail passes through it rather
 * than hanging Access off the project as if it were still a peer tab.
 */
function projectAccessCrumbs(
  segments: readonly string[],
  context: BreadcrumbContext,
): readonly Crumb[] {
  const rawProjectId = segments[1] ?? "";
  const projectBase = `/projects/${rawProjectId}`;
  return [
    { label: PROJECTS.label, href: PROJECTS.to },
    { label: context.projectName ?? decodeURIComponent(rawProjectId), href: projectBase },
    { label: SETTINGS.label, href: `${projectBase}/settings` },
    { label: segmentLabel("access") },
  ];
}

/**
 * Derives the trail from the path alone, so a page gets a breadcrumb without
 * registering anything. The root crumb is always Overview; every other crumb
 * is a link only when its own path is a route.
 */
export function routeBreadcrumbs(
  pathname: string,
  context: BreadcrumbContext = {},
): readonly Crumb[] {
  const segments = pathSegments(pathname);
  if (segments.length === 0) return [{ label: OVERVIEW.label }];

  const crumbs: Crumb[] = [{ label: OVERVIEW.label, href: OVERVIEW.to }];
  if (segments[0] === "agents") {
    crumbs.push(...agentCrumbs(segments, context));
    return crumbs;
  }
  const compatibility = compatibilitySettingsCrumbs(pathname);
  if (compatibility !== undefined) {
    crumbs.push(...compatibility);
    return crumbs;
  }
  if (segments[0] === "projects" && segments.length === 3 && segments[2] === "access") {
    crumbs.push(...projectAccessCrumbs(segments, context));
    return crumbs;
  }
  appendSegments(crumbs, segments, "", function label(segment, index): string {
    // The project id segment shows the project's name when the catalog cache
    // already holds it, and the identifier the user navigated to otherwise.
    if (index === 1 && segments[0] === "projects" && context.projectName !== undefined) {
      return context.projectName;
    }
    return segmentLabel(segment);
  });
  return crumbs;
}
